import { useCallback, useEffect, useMemo, useState } from 'react';
import './dashShared.css';
import './GlobalMain.css';
import { API_URL } from '../config/api';
import { readApiJson, PERMISSION_DENIED_MESSAGE } from '../utils/apiErrors';
import { GlobalDashboard } from './Global_Elements/GlobalDashboard';
import { GlobalSummary } from './Global_Elements/GlobalSummary';
import { GlobalHcNonHc } from './Global_Elements/GlobalHcNonHc';
import { departmentCategoryOptions, shortDeptLabel } from './Global_Elements/globalReporting';
import { DevOverlay } from '../components/DevOverlay/DevOverlay';
import { MultiSelectFilter } from '../components/MultiSelectFilter/MultiSelectFilter';

const TABS = [
    { id: 'dashboard', label: 'Dashboard', dot: '#059669' },
    { id: 'summary', label: 'Summary', dot: '#d97706' },
    { id: 'hc-nonhc-360', label: 'HC & Non-HC 360', dot: '#6d28d9' },
];

// Single shared fetch for all 3 tabs — unlike a single department's own
// Main.js (which hits separate HC/Non-HC/summary endpoints per tab),
// global_views/summary.py already returns everything (combined Total row,
// every department's own category rows, and the department-level HC/Non-HC
// breakdown) in one response, so there's nothing to gain from splitting it.
function GlobalMain({ onDataLoaded }) {
    const [tab, setTab] = useState('dashboard');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    // Every filter below is multi-select (array of selected values) except
    // the From/To custom date range, which stays a single value each. Empty
    // array means "no restriction" (same meaning as the old '' default) for
    // every filter except Fiscal Year, which always needs at least one mode
    // selected to know what to show — it defaults to ['current'] instead and
    // the UI doesn't allow emptying it out (see the Fiscal Year filter below).
    const [yearMode, setYearMode] = useState(['current']);
    const [quarter, setQuarter] = useState([]);
    const [period, setPeriod] = useState([]);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [department, setDepartment] = useState([]);
    const [category, setCategory] = useState([]);
    // Total/Headcount/Non-Headcount/Commission — same "Category" filter every
    // individual department dashboard's own filter bar has, applied
    // client-side here (applyCostCategory in globalReporting.js) since
    // global_summary already returns each row's own .hc/.nonhc/.commission
    // split, no extra fetch needed.
    const [costCategory, setCostCategory] = useState([]);
    // Status/HC Type/Country/Sub-Category — same filter set every individual
    // department dashboard's own filter bar has, applied server-side here
    // the same way (global_views/summary.py's _apply_equality_filters), so
    // changing one of these refetches with the new query param.
    const [hcType, setHcType] = useState([]);
    const [status, setStatus] = useState([]);
    const [country, setCountry] = useState([]);
    const [subCategory, setSubCategory] = useState([]);
    const [vendorName, setVendorName] = useState([]);

    const query = useMemo(() => {
        const params = new URLSearchParams();
        if (hcType.length) params.set('hc_type', hcType.join(','));
        if (status.length) params.set('status', status.join(','));
        if (country.length) params.set('country', country.join(','));
        if (subCategory.length) params.set('sub_category', subCategory.join(','));
        if (vendorName.length) params.set('name', vendorName.join(','));
        return params.toString();
    }, [hcType, status, country, subCategory, vendorName]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/global/summary/?${query}`, { credentials: 'include' });
            setData(await readApiJson(res, 'The Global API could not be loaded.'));
            onDataLoaded?.();
        } catch (err) {
            setMessage(err.message === PERMISSION_DENIED_MESSAGE ? err.message : `${err.message} Check that Django is running on port 8000.`);
        } finally {
            setLoading(false);
        }
    }, [onDataLoaded, query]);

    useEffect(() => { load(); }, [load]);

    const resetFilters = () => {
        setYearMode(['current']); setQuarter([]); setPeriod([]);
        setCustomStart(''); setCustomEnd(''); setDepartment([]); setCategory([]);
        setCostCategory([]);
        setHcType([]); setStatus([]); setCountry([]); setSubCategory([]); setVendorName([]);
    };

    const customRange = customStart && customEnd ? { start: customStart, end: customEnd } : null;
    const effectiveQuarter = customRange ? [] : quarter;
    const { departmentOptions, categoryOptions } = data?.has_data ? departmentCategoryOptions(data, department) : { departmentOptions: [], categoryOptions: [] };
    const hcTypeOptions = data?.filters?.hc_type || [];
    const statusOptions = data?.filters?.status || [];
    const countryOptions = data?.filters?.country || [];
    // Sub-Category is structurally a Non-HC-only dimension (Headcount
    // records carry no sub_category field at all), same as every individual
    // department dashboard's own Category/Sub-Category filter pair — forced
    // empty once Category is narrowed to exclude Non-Headcount, instead of
    // still listing every Non-HC sub-category as if it applied.
    const nonhcExcluded = costCategory.length > 0 && !costCategory.includes('nonhc');
    const subCategoryOptions = nonhcExcluded ? [] : (data?.filters?.sub_category || []);
    // Same Non-HC-only dimension Sub-Category already is, unioned across
    // every visible department's own vendor names.
    const vendorNameOptions = nonhcExcluded ? [] : (data?.filters?.vendor_name || []);

    if (loading) {
        return <div className="esd global-dashboard"><div className="gd-loading">Loading Global…</div></div>;
    }
    if (message) {
        return <div className="esd global-dashboard"><div className="gd-error">{message}</div></div>;
    }
    if (!data?.has_data) {
        return <div className="esd global-dashboard"><div className="gd-loading">{data?.message || 'No department data available yet.'}</div></div>;
    }

    return (
        <div className="dash-shell global-dashboard esd">
            <DevOverlay name="TabNavigation">
                <div className="dash-subnav">
                    <div className="dash-tabs">
                        {TABS.map(t => (
                            <button
                                key={t.id}
                                className={`dash-tab${tab === t.id ? ' active' : ''}`}
                                onClick={() => setTab(t.id)}
                            >
                                <span
                                    className="dash-tab-dot"
                                    style={{ background: tab === t.id ? t.dot : 'transparent', border: `2px solid ${t.dot}` }}
                                />
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </DevOverlay>

            {/* Shared by all 3 tabs — Department/Category narrow every table
                and KPI tile on every tab to one or more departments and/or
                their own categories; Category options are limited to
                whichever department(s) are selected, since category names
                don't mean anything shared across departments. */}
            <DevOverlay name="FilterBar">
                <div className="dash-filterbar">

                    <MultiSelectFilter
                        label="Department"
                        options={departmentOptions.map(d => ({ value: d, label: shortDeptLabel(d) }))}
                        selected={department}
                        onChange={vals => { setDepartment(vals); setCategory([]); }}
                    />

                    <MultiSelectFilter label="Department Sub-Category" options={categoryOptions} selected={category} onChange={setCategory} />

                    <MultiSelectFilter
                        label="Fiscal Year"
                        options={[
                            { value: 'current', label: data.current_year ? `FY ${data.current_year}` : 'Current Year' },
                            { value: 'prior', label: data.prior_year ? `FY ${data.prior_year}` : 'Prior Year' },
                        ]}
                        selected={yearMode}
                        onChange={vals => setYearMode(vals.length ? vals : ['current'])}
                    />

                    <MultiSelectFilter
                        label="Period"
                        options={[{ value: 'fy', label: 'Full Year' }, { value: 'ytd', label: 'Year to Date' }]}
                        selected={period}
                        onChange={setPeriod}
                    />

                    <MultiSelectFilter
                        label="Quarter"
                        options={['Q1', 'Q2', 'Q3', 'Q4']}
                        selected={effectiveQuarter}
                        onChange={vals => { setQuarter(vals); if (vals.length) { setCustomStart(''); setCustomEnd(''); } }}
                        disabled={!!customRange}
                    />

                    <label className="dash-filter-group">
                        <span className="dash-filter-label">From</span>
                        <input
                            className="dash-filter-month" type="month" value={customStart}
                            onChange={e => { setCustomStart(e.target.value); setQuarter([]); }}
                        />
                    </label>
                    <label className="dash-filter-group">
                        <span className="dash-filter-label">To</span>
                        <input
                            className="dash-filter-month" type="month" value={customEnd}
                            onChange={e => { setCustomEnd(e.target.value); setQuarter([]); }}
                        />
                    </label>

                    <div className="dash-fb-divider" />

                    <MultiSelectFilter
                        label="Category"
                        options={[
                            { value: 'hc', label: 'Headcount' },
                            { value: 'nonhc', label: 'Non-Headcount' },
                            { value: 'commission', label: 'Commission' },
                        ]}
                        selected={costCategory}
                        onChange={vals => {
                            setCostCategory(vals);
                            // Sub-Category and Vendor Name have no meaning once the
                            // selection excludes Non-Headcount entirely (both are
                            // Non-HC-only fields) — clear any stale selection so it
                            // doesn't keep narrowing the KPIs by a filter that no
                            // longer shows in its own dropdown at all.
                            if (vals.length && !vals.includes('nonhc')) { setSubCategory([]); setVendorName([]); }
                        }}
                        title="No selection = Total (Headcount + Non-Headcount + Commission combined)"
                    />

                    <MultiSelectFilter label="Status" options={statusOptions} selected={status} onChange={setStatus} />

                    <MultiSelectFilter label="HC Category" options={hcTypeOptions} selected={hcType} onChange={setHcType} />

                    <MultiSelectFilter label="Country" options={countryOptions} selected={country} onChange={setCountry} />

                    <MultiSelectFilter
                        label="Sub-Category(Non-HC)"
                        options={subCategoryOptions}
                        selected={subCategory}
                        disabled={nonhcExcluded}
                        title={nonhcExcluded
                            ? 'Sub-Category does not apply to Headcount or Commission records.'
                            : 'Sub-Category only applies to Non-Headcount records — picking any switches Category to Non-Headcount so the KPIs aren\'t silently blended with unfiltered Headcount/Commission spend.'}
                        onChange={vals => {
                            setSubCategory(vals);
                            // Vendor Name's own option list is scoped to this Sub-Category, so
                            // a vendor picked under the old selection may no longer be valid.
                            setVendorName([]);
                            // Sub-Category is a Non-HC-only dimension — Headcount/Commission
                            // records have no sub-category to filter, so leaving Category
                            // unrestricted (or Headcount/Commission-inclusive) would keep
                            // showing full, unfiltered spend blended in with the
                            // narrowly-filtered Non-HC slice, making the KPI barely move and
                            // look like the filter "did nothing." Auto-switching to
                            // Non-Headcount-only makes every KPI reflect just the selected
                            // sub-category, with no silent blending.
                            if (vals.length) setCostCategory(['nonhc']);
                        }}
                    />

                    <MultiSelectFilter
                        label="Vendor Name"
                        options={vendorNameOptions}
                        selected={vendorName}
                        disabled={nonhcExcluded}
                        title={nonhcExcluded
                            ? 'Vendor Name does not apply to Headcount or Commission records.'
                            : 'Vendor Name only applies to Non-Headcount records — picking any switches Category to Non-Headcount so the KPIs aren\'t silently blended with unfiltered Headcount/Commission spend.'}
                        onChange={vals => {
                            setVendorName(vals);
                            // Same Non-HC-only auto-switch Sub-Category's own onChange does,
                            // for the same reason — see its comment above.
                            if (vals.length) setCostCategory(['nonhc']);
                        }}
                    />

                    <button className="dash-filter-reset" onClick={resetFilters}>↩ Reset</button>
                </div>
            </DevOverlay>

            <div className="dash-content">
                {tab === 'dashboard' && (
                    <DevOverlay name="GlobalDashboard">
                        <GlobalDashboard data={data} quarter={effectiveQuarter} customRange={customRange} period={period} yearMode={yearMode} department={department} category={category} costCategory={costCategory} />
                    </DevOverlay>
                )}
                {tab === 'summary' && (
                    <DevOverlay name="GlobalSummary">
                        <GlobalSummary data={data} quarter={effectiveQuarter} customRange={customRange} period={period} yearMode={yearMode} department={department} category={category} costCategory={costCategory} />
                    </DevOverlay>
                )}
                {tab === 'hc-nonhc-360' && (
                    <DevOverlay name="GlobalHcNonHc">
                        <GlobalHcNonHc data={data} department={department} costCategory={costCategory} />
                    </DevOverlay>
                )}
            </div>
        </div>
    );
}

export default GlobalMain;
